import * as Sentry from "@sentry/react";
import type { AnyRouter } from "@tanstack/react-router";

/**
 * initSentry initializes the Sentry React SDK.
 *
 * It is a no-op when VITE_SENTRY_DSN is empty (e.g. local dev), so the app
 * builds and runs exactly as before without a DSN configured.
 *
 * Capture is intentionally maximal: full PII, fully-unmasked Session Replay
 * (including password fields, per explicit product decision), 100% traces,
 * and browser profiling.
 */
export function initSentry(router: AnyRouter): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    environment: import.meta.env.MODE,

    // Maximal capture (per locked decisions).
    sendDefaultPii: true,

    integrations: [
      // Browser tracing wired to the TanStack Router instance so navigations
      // produce proper pageload/navigation transactions with route names.
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      // Session Replay — fully unmasked, including inputs and media.
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
      // Browser (UI) profiling.
      Sentry.browserProfilingIntegration(),
    ],

    // Performance + profiling sample rates.
    tracesSampleRate: 1.0,
    profilesSampleRate: 1.0,

    // Continue the distributed trace into the backend services.
    tracePropagationTargets: [
      "https://api.hotrecall.com",
      "https://mcp.hotrecall.com",
    ],

    // Session Replay sampling.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}
